import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  currentSelectedLanguage,
  storeRShieldOptions,
  updateRShieldOption,
  RSHIELD_FOCUS_LOSS_LIMIT_MIN,
  RSHIELD_FOCUS_LOSS_LIMIT_MAX,
  RSHIELD_WHEN_LIMIT_REACHED,
} from "../../../../StateManagement/actions";
import "./RShieldOptions.css";


function InfoIcon({ id, text, disabled }) {
  const showTooltip = !disabled && Boolean(text);
  return (
    <>
      <span className="material-icons info-tooltip-f authinfo rshield-info" id={id} data-toggle={showTooltip ? "tooltip" : undefined} data-html={showTooltip ? "true" : undefined}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        {" "} info{" "}
      </span>
      {showTooltip && (
        <ReactTooltip className="rshield-tooltip" opacity={1} anchorId={id} place="bottom">
          <table className="tooltip-table">
            <tbody>
              <tr>
                <td>
                  <div className="d-flex flex-column p-1">
                    <p className="tooltip-text mb-0">{text}</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </ReactTooltip>
      )}
    </>
  );
}

/**
 * Checkbox rendered with the same markup the rest of the app uses
 * (label > input + span.cr > i.cr-icon) so it inherits the existing styling.
 */
function RShieldCheckbox({ id, label, info, checked, disabled, onChange }) {
  return (
    <div className={`checkbox rshield-checkbox ${disabled ? "rshield-disabled" : ""}`}>
      <label className="ps-0 mb-0" htmlFor={id}>
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="cr">
          <i className="cr-icon material-icons">check</i>
        </span>
      </label>
      <span className="rshield-label">
        <span className="rshield-label-text">{label}</span>
        <InfoIcon id={`${id}_info`} text={info} disabled={disabled} />
      </span>
    </div>
  );
}
function FocusLossLimitDropdown({ id, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="rshield-limit-dropdown" ref={wrapperRef}>
      <button type="button" id={id} className="rshield-limit-toggle" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((prev) => !prev)}>
        <span>{value}</span>
        <i className="material-icons rshield-limit-caret">arrow_drop_down</i>
      </button>
      {open && (
        <ul className="rshield-limit-menu" role="listbox">
          {options.map((option) => (
            <li key={option} role="option" aria-selected={option === value} className={`rshield-limit-option ${option === value ? "active" : ""}`}
              onClick={() => { onChange(option); setOpen(false); }}>
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LabelledOptionDropdown({ id, value, options, disabled, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((option) => option.value === value) || options[0];

  return (
    <div className={`rshield-limit-dropdown rshield-when-limit-dropdown ${disabled ? "rshield-disabled" : ""}`} ref={wrapperRef}>
      <button type="button" id={id} className="rshield-limit-toggle" aria-haspopup="listbox" aria-expanded={open}
        disabled={disabled} onClick={() => !disabled && setOpen((prev) => !prev)}>
        <span>{selected.label}</span>
        <i className="material-icons rshield-limit-caret">arrow_drop_down</i>
      </button>
      {open && !disabled && (
        <ul className="rshield-limit-menu rshield-when-limit-menu" role="listbox">
          {options.map((option) => (
            <li key={option.value} role="option" aria-selected={option.value === value} className={`rshield-limit-option ${option.value === value ? "active" : ""}`}
              onClick={() => { onChange(option.value); setOpen(false); }}>
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
// 1 .. 30
const focusLossLimitOptions = Array.from(
  { length: RSHIELD_FOCUS_LOSS_LIMIT_MAX - RSHIELD_FOCUS_LOSS_LIMIT_MIN + 1 },
  (_, index) => RSHIELD_FOCUS_LOSS_LIMIT_MIN + index
);

const getWhenLimitReachedOptions = (selectedLanguage) => [
  { value: RSHIELD_WHEN_LIMIT_REACHED.END_SESSION, label: selectedLanguage.LstLanguageCodes.UI_EndSession },
  { value: RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE, label: selectedLanguage.LstLanguageCodes.UI_Reauthenticate },
];

const RShieldOptionsModal = React.forwardRef(({ onClose }, ref) => {
  const dispatch = useDispatch();
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const rShield = useSelector((state) => state.rShield);

  const [isOpen, setIsOpen] = useState(false);
  // Snapshot taken when the popup is opened so Cancel can roll back.
  const initialValues = useRef(null);

  const currentOptions = {
    focusProtection: rShield.focusProtection,
    resumeOnFullScreen: rShield.resumeOnFullScreen,
    focusLossLimit: rShield.focusLossLimit,
    focusLossLimitValue: rShield.focusLossLimitValue,
    banReader: rShield.banReader,
    notifySender: rShield.notifySender,
    whenLimitReached: rShield.whenLimitReached,
  };
  const latestOptions = useRef(currentOptions);
  latestOptions.current = currentOptions;

  const onChangeHandler = (type, value) => {
    if (type === "focusLossLimit" && value === true) {
      dispatch(updateRShieldOption({ type: "focusLossLimitValue", value: RSHIELD_FOCUS_LOSS_LIMIT_MIN }));
    }
    if (type === "focusLossLimit" && value === false) {
      dispatch(updateRShieldOption({ type: "whenLimitReached", value: RSHIELD_WHEN_LIMIT_REACHED.END_SESSION }));
    }
    dispatch(updateRShieldOption({ type, value }));
  };

  const onCancelHandler = () => {
    if (initialValues.current) {
      dispatch(storeRShieldOptions(initialValues.current));
    }
    setIsOpen(false);
    onClose(false, initialValues.current, false);
  };

  const onSaveHandler = () => {
    const saved = { ...latestOptions.current };
    initialValues.current = saved;
    setIsOpen(false);
    onClose(false, saved, true);
  };

  useImperativeHandle(ref, () => ({
    showModal: () => {
      initialValues.current = { ...latestOptions.current };
      setIsOpen(true);
    },
    closeModal: () => {
      setIsOpen(false);
    },
  }));

  if (!isOpen) return null;

  const focusOn = rShield.focusProtection === true;
  const focusLossLimitOn = rShield.focusLossLimit === true;
  const focusLossLimitValue = rShield.focusLossLimitValue ?? RSHIELD_FOCUS_LOSS_LIMIT_MIN;
  const whenLimitReachedSelection = rShield.whenLimitReached === RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE ? RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE : RSHIELD_WHEN_LIMIT_REACHED.END_SESSION;
  const whenLimitReachedOptions = getWhenLimitReachedOptions(selectedLanguage);
  const whenLimitReachedInfoText = (
    <>
      <span className="d-block mb-1">
        <strong>{selectedLanguage.LstLanguageCodes.UI_EndSession}:</strong> {selectedLanguage.LstLanguageCodes.UI_EndSessionInfo}
      </span>
      <span className="d-block">
        <strong>{selectedLanguage.LstLanguageCodes.UI_Reauthenticate}:</strong> {selectedLanguage.LstLanguageCodes.UI_ReauthenticateInfo}
      </span>
    </>
  );
  return createPortal(
    <div className="rshield modal fade show d-block" id="rShieldModal" style={{ zIndex: 2000 }} role="dialog">
      <div className="modal-dialog modal-dialog-centered" style={{ zIndex: "2000 !important" }}>
        <div className="modal-content rshield-content">
          <div className="modal-header">
            <h5 className="d-flex align-items-center fw-bold mb-0 rshield-title">
              {(selectedLanguage.LstLanguageCodes.UI_RShieldOptions || "RShield Options")}
            </h5>
            <button type="button" className="btn-close" onClick={onCancelHandler}></button>
          </div>

          <div className="modal-body">
            {/* ---------------- Focus Protection ---------------- */}
            <div className="rshield-section">
              <div className="rshield-section-head">
                <span className="rshield-section-title">
                  {selectedLanguage.LstLanguageCodes.UI_FocusProtection}
                </span>
                <InfoIcon id="rshieldFocusProtectionInfo" text={selectedLanguage.LstLanguageCodes.UI_FocusProtectionInfo} />
                {/* Same markup as the RPD Live toggle on the Send / Generate tab:
                    the input is visually hidden and label.slider is the control. */}
                <div className="switch rshieldSwitch ms-auto">
                  <input
                    type="checkbox"
                    id="chkRShieldFocusProtection"
                    checked={focusOn}
                    readOnly
                  />
                  <label
                    className="slider"
                    onClick={() => onChangeHandler("focusProtection", !focusOn)}></label>
                </div>
              </div>


              <div className="rshield-inline">
                <RShieldCheckbox
                  id="chkRShieldResumeOnFullScreen"
                  label={selectedLanguage.LstLanguageCodes.UI_ViewOnlyinFullScreen}
                  info={selectedLanguage.LstLanguageCodes.UI_ViewOnlyinFullScreenInfo}
                  checked={rShield.resumeOnFullScreen === true}
                  disabled={!focusOn}
                  onChange={(value) => onChangeHandler("resumeOnFullScreen", value)}
                />

                <div className="rshield-inline-field">
                  <RShieldCheckbox
                    id="chkRShieldFocusLossLimit"
                    label={selectedLanguage.LstLanguageCodes.UI_FocusoffLimit}
                    info={selectedLanguage.LstLanguageCodes.UI_FocusLossLimitInfo}
                    checked={focusLossLimitOn}
                    disabled={!focusOn}
                    onChange={(value) => onChangeHandler("focusLossLimit", value)}
                  />

                  {/* Dropdown only appears once Focus Loss Limit is ticked. */}
                  {focusOn && focusLossLimitOn && (
                    <FocusLossLimitDropdown id="ddlRShieldFocusLossLimit" value={Number(focusLossLimitValue)} options={focusLossLimitOptions}
                      onChange={(value) => onChangeHandler("focusLossLimitValue", value)}
                    />
                  )}
                </div>
              </div>
              <div className={`rshield-when-limit-reached ${!focusOn || !focusLossLimitOn ? "rshield-disabled" : ""}`}>
                <div className="rshield-when-limit-reached-label">
                  <span className="rshield-inline-label">
                    {selectedLanguage.LstLanguageCodes.UI_WhenLimitisReached}
                  </span>
                </div>
                <InfoIcon id="rshieldWhenLimitReachedInfo" text={whenLimitReachedInfoText} disabled={!focusOn || !focusLossLimitOn} />
                <LabelledOptionDropdown id="ddlRShieldWhenLimitReached" value={whenLimitReachedSelection} options={whenLimitReachedOptions}
                  disabled={!focusOn || !focusLossLimitOn} onChange={(value) => onChangeHandler("whenLimitReached", value)}
                />
              </div>
            </div>

            {/* ---------------- Capture Detection ---------------- */}
            <div className="rshield-section">
              <div className="rshield-section-head">
                <span className="rshield-section-title">
                  {selectedLanguage.LstLanguageCodes.UI_CaptureDetection}
                </span>
                <InfoIcon id="rshieldCaptureDetectionInfo" text={selectedLanguage.LstLanguageCodes.UI_CaptureDetectionInfo} />
              </div>

              <div className="rshield-inline">
                <RShieldCheckbox
                  id="chkRShieldBanReader"
                  label={selectedLanguage.LstLanguageCodes.UI_BanReader}
                  info={selectedLanguage.LstLanguageCodes.UI_BanReaderInfo}
                  checked={rShield.banReader === true}
                  disabled={false}
                  onChange={(value) => onChangeHandler("banReader", value)}
                />
                <RShieldCheckbox
                  id="chkRShieldNotifySender"
                  label={selectedLanguage.LstLanguageCodes.UI_NotifySender}
                  info={selectedLanguage.LstLanguageCodes.UI_NotifySenderInfo}
                  checked={rShield.notifySender === true}
                  disabled={false}
                  onChange={(value) => onChangeHandler("notifySender", value)}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer rshield-footer">
            <button
              type="button"
              title={selectedLanguage.LstLanguageCodes.UI_Cancel}
              className="btn btn-danger px-2 me-2 ms-1 w-140p truncate"
              onClick={onCancelHandler}>
              {selectedLanguage.LstLanguageCodes.UI_Cancel}
            </button>
            <button
              type="button"
              title={selectedLanguage.LstLanguageCodes.UI_Save}
              className="btn btn-primary px-2 ms-1 w-140p truncate"
              onClick={onSaveHandler}>
              {selectedLanguage.LstLanguageCodes.UI_Save}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById("modal-popup")
  );
});

export default RShieldOptionsModal;
